import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";

// ════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════
function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function fmt(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN");
}

function todayDMY() {
  const d = new Date();
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function agingLabel(createdAt, startDate) {
  const raw = startDate || createdAt;
  if (!raw) return null;
  const start = raw.toDate ? raw.toDate() : new Date(raw);
  const now = new Date();
  const totalDays = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  if (totalDays < 1) return "Open for less than a day";
  const months = Math.floor(totalDays / 30);
  const days = totalDays % 30;
  if (months === 0) return `Open for ${days}d`;
  if (days === 0) return `Open for ${months}m`;
  return `Open for ${months}m ${days}d`;
}

function dueDateBadge(due) {
  if (!due) return { label: "No due date", color: "#555570", bg: "#1a1a24", border: "#2e2e40" };
  const d = new Date(due);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, color: "#ff5252", bg: "#ff525218", border: "#ff525240" };
  if (diff === 0) return { label: "Due today", color: "#ffa726", bg: "#ffa72618", border: "#ffa72640" };
  if (diff <= 3) return { label: `Due in ${diff}d`, color: "#ffa726", bg: "#ffa72618", border: "#ffa72640" };
  return { label: `Due ${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`, color: "#8888a8", bg: "#1a1a24", border: "#2e2e40" };
}

// Shared input style
const inputStyle = {
  width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
  borderRadius: "10px", padding: "10px 12px", color: "#e8e8f0",
  fontSize: "14px", outline: "none", boxSizing: "border-box",
};
const labelStyle = { fontSize: "11px", color: "#8888a8", marginBottom: "5px", display: "block" };

function ModalShell({ title, onClose, children, maxHeight }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000cc", zIndex: 1000,
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }}>
      <div style={{
        background: "#1a1a24", borderRadius: "20px 20px 0 0",
        width: "100%", maxWidth: "640px",
        maxHeight: maxHeight || "90vh",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>
        {/* Sticky header — always visible, never scrolls away */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "16px 20px", borderBottom: "1px solid #2e2e40",
          flexShrink: 0,
        }}>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8888a8", fontSize: "22px", cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        {/* Scrollable content */}
        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// NOTIFICATIONS HELPER — writes directly to the same "notifications"
// collection your NotificationBell.jsx already reads from.
// No changes needed in NotificationBell.jsx at all.
// ════════════════════════════════════════════════════════════════
async function writeNotification({ toUid, message, type }) {
  if (!toUid) return;
  try {
    await addDoc(collection(db, "notifications"), {
      toUid, message, type,
      isRead: false,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Notification write error:", e);
  }
}

// ════════════════════════════════════════════════════════════════
// EXISTING — Add (checklist) Task Modal — UNCHANGED from your original
// ════════════════════════════════════════════════════════════════
function AddTaskModal({ onClose, onDone, user }) {
  const [text, setText] = useState("");
  const [due, setDue]   = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function handleSubmit() {
    if (!text.trim()) { setErr("Task description is required."); return; }
    setSaving(true);
    try {
      const snap = await getDocs(collection(db, "tasks"));
      const maxId = snap.docs.reduce((m, d) => Math.max(m, d.data().id || 0), 0);
      await addDoc(collection(db, "tasks"), {
        id: maxId + 1,
        text: text.trim(),
        due: due || "",
        done: false,
        doneNote: "",
        addedBy: user.name,
        addedAt: serverTimestamp(),
      });
      onDone();
    } catch (e) {
      setErr("Failed to add task. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Add Task" onClose={onClose}>
      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Task Description</label>
        <textarea
          value={text} onChange={e => setText(e.target.value)}
          placeholder="What needs to be done?"
          rows={3}
          style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
        />
      </div>
      <div style={{ marginBottom: "20px" }}>
        <label style={labelStyle}>Due Date (optional)</label>
        <input type="date" value={due} onChange={e => setDue(e.target.value)} style={inputStyle} />
      </div>
      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}
      <button onClick={handleSubmit} disabled={saving} style={{
        width: "100%", padding: "14px", background: saving ? "#2e2e40" : "#6c63ff",
        border: "none", borderRadius: "12px", color: "#fff",
        fontSize: "15px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
      }}>
        {saving ? "Adding…" : "Add Task"}
      </button>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════
// EXISTING — Done/Reopen Modal — UNCHANGED from your original
// ════════════════════════════════════════════════════════════════
function DoneModal({ task, onClose, onDone, user }) {
  const [note, setNote] = useState(task.doneNote || "");
  const [saving, setSaving] = useState(false);
  const markDone = !task.done;

  async function handleSubmit() {
    setSaving(true);
    try {
      await updateDoc(doc(db, "tasks", task.docId), {
        done: markDone,
        doneNote: note.trim(),
        doneAt: markDone ? serverTimestamp() : null,
        doneBy: markDone ? user.name : null,
      });
      onDone();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title={markDone ? "Mark Complete" : "Reopen Task"} onClose={onClose}>
      <div style={{ background: "#0f0f13", borderRadius: "12px", padding: "12px", marginBottom: "16px", fontSize: "13px", color: "#e8e8f0" }}>
        {task.text}
      </div>
      <div style={{ marginBottom: "20px" }}>
        <label style={labelStyle}>{markDone ? "Completion Note (optional)" : "Reason for reopening (optional)"}</label>
        <input
          type="text" value={note} onChange={e => setNote(e.target.value)}
          placeholder={markDone ? "e.g. Done by Vijay on 12 Jun" : "e.g. Needs revision"}
          style={inputStyle}
        />
      </div>
      <button onClick={handleSubmit} disabled={saving} style={{
        width: "100%", padding: "14px",
        background: saving ? "#2e2e40" : (markDone ? "#4caf50" : "#ffa726"),
        border: "none", borderRadius: "12px", color: "#fff",
        fontSize: "15px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
      }}>
        {saving ? "Saving…" : (markDone ? "✅ Mark Complete" : "🔄 Reopen Task")}
      </button>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════
// HELPER — max due date = today + 7 days (for event creation)
// ════════════════════════════════════════════════════════════════
function maxDueDate() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}
function minDueDate() {
  return new Date().toISOString().slice(0, 10);
}

// ════════════════════════════════════════════════════════════════
// REDESIGNED — Create Event Modal
// - Two assignee dropdowns (both mandatory)
// - Super admin (role = "super admin") excluded from both lists
// - Radio button: Society Work | General Event
// - Estimated Budget shown only for Society Work → auto-calculates per flat share
// - Notifications: broadcast to all + individual to both assignees
//   + individual to each flat owner for Society Work
// ════════════════════════════════════════════════════════════════
function CreateEventModal({ onClose, onDone, user }) {
  const [title, setTitle]               = useState("");
  const [peopleList, setPeopleList]     = useState([]);
  const [assignedUid1, setAssignedUid1] = useState("");
  const [assignedUid2, setAssignedUid2] = useState("");
  const [eventType, setEventType]       = useState("general"); // "society" | "general"
  const [budget, setBudget]             = useState("");
  const [dueDate, setDueDate]           = useState("");
  const [startDate, setStartDate]       = useState(new Date().toISOString().split("T")[0]); // defaults to today
  const [flatsList, setFlatsList]       = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(true);
  const [saving, setSaving]             = useState(false);
  const [err, setErr]                   = useState("");

  const isSocietyWork  = eventType === "society";
  const numFlats       = flatsList.length;
  const perFlatAmt     = isSocietyWork && budget && Number(budget) > 0 && numFlats > 0
    ? Math.round(Number(budget) / numFlats)
    : null;

  useEffect(() => {
    async function loadData() {
      try {
        const [usersSnap, flatsSnap] = await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(collection(db, "flats")),
        ]);
        // Exclude inactive users AND super admin
        const people = usersSnap.docs
          .map(d => ({ uid: d.id, ...d.data() }))
          .filter(p => p.active !== false && p.role !== "super admin")
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setPeopleList(people);
        setFlatsList(flatsSnap.docs.map(d => ({ flatId: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingPeople(false);
      }
    }
    loadData();
  }, []);

  function formatDateDDMMMYYYY(d = new Date()) {
    const day    = String(d.getDate()).padStart(2, "0");
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const month  = months[d.getMonth()];
    const year   = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  async function handleSubmit() {
    if (!title.trim())   { setErr("Event title is required."); return; }
    if (!assignedUid1)   { setErr("Please select the first assigned person."); return; }
    if (assignedUid2 && assignedUid1 === assignedUid2) { setErr("Both assigned persons must be different."); return; }
    if (!dueDate)        { setErr("Expected closure date is required."); return; }
    if (isSocietyWork && (!budget || Number(budget) <= 0)) { setErr("Please enter the estimated budget for society work."); return; }

    setSaving(true);
    try {
      const person1 = peopleList.find(p => p.uid === assignedUid1);
      const person2 = assignedUid2 ? peopleList.find(p => p.uid === assignedUid2) : null;
      const name1   = person1 ? person1.name : "Unknown";
      const name2   = person2 ? person2.name : "";
      const bothNames = name2 ? `${name1} & ${name2}` : name1;

      // If Person 2 is empty, find President to notify
      const presidentRecord = !assignedUid2
        ? peopleList.find(p => p.role === "President")
        : null;

      if (isSocietyWork) {
        // ── Society Work → adhocEvents + adhocPayments per flat ──
        // Auto-append today's date (dd-mmm-yyyy) to the title so events are easy to tell apart.
        const finalTitle = `${title.trim()} - ${formatDateDDMMMYYYY()}`;

        // FIX — TasksScreen 3.1 (design frozen 2026-07-13):
        // The event + every flat's due are now created in ONE Firestore
        // batch — either all 13 (1 event + 12 flats) writes go through
        // together, or none do. Before this fix, each write happened one
        // after another with nothing tying them together, so a mid-way
        // failure could leave some flats billed and others silently missed.
        const batch = writeBatch(db);

        const adhocRef = doc(collection(db, "adhocEvents"));
        batch.set(adhocRef, {
          title:           finalTitle,
          category:        "Society Work",
          societyId:       user.societyId,
          scope:           "building",
          estimatedBudget: Number(budget),
          perFlatAmt:      perFlatAmt,
          totalAmt:        Number(budget),
          assignedToUid:   assignedUid1,
          assignedToName:  name1,
          assignedToUid2:  assignedUid2,
          assignedToName2: name2,
          dueDate,
          startDate,
          createdByUid:    user.id || user.uid,
          createdBy:       user.name,
          status:          "open",
          createdAt:       serverTimestamp(),
        });

        // One adhocPayment per flat — same batch as the event above
        flatsList.forEach(f => {
          const payRef = doc(collection(db, "adhocPayments"));
          batch.set(payRef, {
            eventId:    adhocRef.id,
            societyId:  user.societyId,
            eventTitle: finalTitle,
            flatId:     f.flatId,
            amount:     perFlatAmt,
            paid:       false,
            paidDate:   "",
            mode:       "",
            paidBy:     "",
            receiptRef: "",
            notes:      "",
            addedBy:    user.name,
            addedAt:    serverTimestamp(),
          });
        });

        await batch.commit();

        // Notifications stay OUTSIDE the safety bundle on purpose — a
        // failed notification should never undo real billing records.
        // If one fails, it's logged quietly here rather than silently
        // lost, so it can still be spotted if needed — but it does not
        // block or roll back the event/dues above, which are already saved.
        try {
          // Broadcast to everyone
          await writeNotification({
            toUid:   "all",
            message: `🏗️ New society work: "${finalTitle}" — ₹${perFlatAmt?.toLocaleString("en-IN")} per flat. Assigned to ${bothNames}.`,
            type:    "adhoc_created",
          });
          // Individual to Person 1
          await writeNotification({
            toUid:   assignedUid1,
            message: `You've been assigned to manage society work: "${finalTitle}"`,
            type:    "event_assigned",
          });
          // Individual to Person 2 (if selected)
          if (assignedUid2) {
            await writeNotification({
              toUid:   assignedUid2,
              message: `You've been assigned to manage society work: "${finalTitle}"`,
              type:    "event_assigned",
            });
          }
          // Notify President if Person 2 was left empty
          if (presidentRecord && presidentRecord.uid !== assignedUid1) {
            await writeNotification({
              toUid:   presidentRecord.uid,
              message: `⚠️ Society work "${finalTitle}" was created with only one assignee. Please review and add a second person if needed.`,
              type:    "event_single_assignee",
            });
          }
        } catch (notifyErr) {
          console.error(
            `Society Work "${finalTitle}" was billed successfully, but one or more notifications failed to send:`,
            notifyErr
          );
        }

      } else {
        // ── General Event → events collection ──
        const eventRef = await addDoc(collection(db, "events"), {
          title:           title.trim(),
          assignedToUid:   assignedUid1,
          assignedToName:  name1,
          assignedToUid2:  assignedUid2,
          assignedToName2: name2,
          dueDate,
          startDate,
          createdByUid:    user.id || user.uid,
          createdByName:   user.name,
          status:          "open",
          createdAt:       serverTimestamp(),
          closedAt:        null,
          closeNote:       "",
        });

        // Broadcast to everyone
        await writeNotification({
          toUid:   "all",
          message: `📋 New event: "${title.trim()}" — assigned to ${bothNames}.`,
          type:    "event_created",
        });
        // Individual to Person 1
        await writeNotification({
          toUid:   assignedUid1,
          message: `You've been assigned to handle: "${title.trim()}"`,
          type:    "event_assigned",
        });
        // Individual to Person 2 (if selected)
        if (assignedUid2) {
          await writeNotification({
            toUid:   assignedUid2,
            message: `You've been assigned to handle: "${title.trim()}"`,
            type:    "event_assigned",
          });
        }
        // Notify President if Person 2 was left empty
        if (presidentRecord && presidentRecord.uid !== assignedUid1) {
          await writeNotification({
            toUid:   presidentRecord.uid,
            message: `⚠️ Event "${title.trim()}" was created with only one assignee. Please review and add a second person if needed.`,
            type:    "event_single_assignee",
          });
        }

        onDone(eventRef.id);
        return;
      }

      onDone();
    } catch (e) {
      console.error(e);
      setErr("Failed to create event. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // People available for second dropdown — exclude whoever is selected in first
  const peopleForDropdown2 = peopleList.filter(p => p.uid !== assignedUid1);

  return (
    <ModalShell title="Create Event" onClose={onClose}>

      {/* ── Field 1: Title ── */}
      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Event Title *</label>
        <input
          type="text" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Ganesh Function 2026"
          style={inputStyle}
        />
      </div>

      {/* ── Field 2: Two Assignees ── */}
      <div style={{ marginBottom: "6px" }}>
        <label style={labelStyle}>Assign To *</label>
        {loadingPeople ? (
          <div style={{ fontSize: "12px", color: "#555570" }}>Loading people…</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <select
              value={assignedUid1}
              onChange={e => { setAssignedUid1(e.target.value); setAssignedUid2(""); }}
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              <option value="">-- Person 1 --</option>
              {peopleList.map(p => (
                <option key={p.uid} value={p.uid}>
                  {p.name} {p.flatId ? `(${p.flatId})` : ""} {p.role ? `· ${p.role}` : ""}
                </option>
              ))}
            </select>
            <select
              value={assignedUid2}
              onChange={e => setAssignedUid2(e.target.value)}
              style={{ ...inputStyle, cursor: "pointer" }}
              disabled={!assignedUid1}
            >
              <option value="">-- Person 2 --</option>
              {peopleForDropdown2.map(p => (
                <option key={p.uid} value={p.uid}>
                  {p.name} {p.flatId ? `(${p.flatId})` : ""} {p.role ? `· ${p.role}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "6px" }}>
          Person 1 is mandatory. Person 2 is optional — if left empty, President will be notified to review.
        </div>
      </div>

      {/* ── Field 3: Event Type ── */}
      <div style={{ marginBottom: "14px", marginTop: "14px" }}>
        <label style={labelStyle}>Event Type *</label>
        <div style={{ display: "flex", gap: "10px" }}>
          {[
            { value: "society", label: "🏗️ Society Work", desc: "Collect from all flats" },
            { value: "general", label: "📋 General Event", desc: "Track income & expenses" },
          ].map(opt => (
            <div
              key={opt.value}
              onClick={() => { setEventType(opt.value); setBudget(""); }}
              style={{
                flex: 1, padding: "12px", borderRadius: "12px", cursor: "pointer",
                border: `1px solid ${eventType === opt.value ? "#6c63ff" : "#2e2e40"}`,
                background: eventType === opt.value ? "#6c63ff18" : "transparent",
              }}
            >
              <div style={{ fontSize: "13px", fontWeight: "700", color: eventType === opt.value ? "#6c63ff" : "#e8e8f0" }}>
                {opt.label}
              </div>
              <div style={{ fontSize: "11px", color: "#555570", marginTop: "3px" }}>
                {opt.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Field 4: Estimated Budget (Society Work only) ── */}
      {isSocietyWork && (
        <div style={{ marginBottom: "14px" }}>
          <label style={labelStyle}>Estimated Budget (₹) *</label>
          <input
            type="number" value={budget} onChange={e => setBudget(e.target.value)}
            placeholder="e.g. 6000"
            style={inputStyle}
          />
          {perFlatAmt && (
            <div style={{ fontSize: "11px", color: "#4caf50", marginTop: "6px", fontWeight: "600" }}>
              💡 ₹{perFlatAmt.toLocaleString("en-IN")} per flat across {numFlats} flats
            </div>
          )}
          {!perFlatAmt && (
            <div style={{ fontSize: "11px", color: "#555570", marginTop: "4px" }}>
              Enter the total budget — per flat share will be calculated automatically.
            </div>
          )}
        </div>
      )}

      {/* ── Field 5: Start Date + Expected Closure Date ── */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Start Date *</label>
          <input
            type="date"
            value={startDate}
            max={new Date().toISOString().split("T")[0]}
            onChange={e => setStartDate(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Expected Closure Date *</label>
          <input
            type="date"
            value={dueDate}
            min={minDueDate()}
            onChange={e => setDueDate(e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>

      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}

      <button onClick={handleSubmit} disabled={saving} style={{
        width: "100%", padding: "14px",
        background: saving ? "#2e2e40" : isSocietyWork ? "#4caf50" : "#6c63ff",
        border: "none", borderRadius: "12px", color: "#fff",
        fontSize: "15px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
      }}>
        {saving ? "Creating…" : isSocietyWork ? "🏗️ Create Society Work" : "📋 Create Event"}
      </button>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════
// NEW — Edit Due Date Modal (President only)
// ════════════════════════════════════════════════════════════════
function EditDueDateModal({ event, onClose, onDone, user }) {
  const [dueDate, setDueDate] = useState(event.dueDate || "");
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState("");

  async function handleSave() {
    if (!dueDate) { setErr("Please select a new due date."); return; }
    setSaving(true);
    try {
      const formattedDate = new Date(dueDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
      const msg = `✏️ "${event.title}" due date changed to ${formattedDate} by ${user.name}`;

      await updateDoc(doc(db, "events", event.docId), {
        dueDate,
        lastEditedBy: user.name,
        lastEditedAt: serverTimestamp(),
      });

      // Notify Treasurer, President, and both assignees specifically
      const notifySnap = await getDocs(query(
        collection(db, "users"),
        where("role", "in", ["President", "Treasurer"])
      ));
      for (const u of notifySnap.docs) {
        await writeNotification({ toUid: u.id, message: msg, type: "event_due_updated" });
      }
      // Notify assignee 1
      if (event.assignedToUid) {
        await writeNotification({ toUid: event.assignedToUid, message: msg, type: "event_due_updated" });
      }
      // Notify assignee 2
      if (event.assignedToUid2) {
        await writeNotification({ toUid: event.assignedToUid2, message: msg, type: "event_due_updated" });
      }

      onDone();
    } catch (e) {
      console.error(e);
      setErr("Failed to update due date. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Edit Expected Closure Date" onClose={onClose}>
      <div style={{ marginBottom: "20px" }}>
        <label style={labelStyle}>New Expected Closure Date</label>
        <input
          type="date"
          value={dueDate}
          min={minDueDate()}
          onChange={e => setDueDate(e.target.value)}
          style={inputStyle}
        />
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "4px" }}>
          A notification will be sent to everyone when you save.
        </div>
      </div>
      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}
      <button onClick={handleSave} disabled={saving} style={{
        width: "100%", padding: "14px", background: saving ? "#2e2e40" : "#6c63ff",
        border: "none", borderRadius: "12px", color: "#fff",
        fontSize: "15px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
      }}>
        {saving ? "Saving…" : "Save Expected Closure Date"}
      </button>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════
// NEW — Add Entry Modal (income or expense) inside an Event
// Only the assigned person can add entries.
// ════════════════════════════════════════════════════════════════
function AddEventEntryModal({ event, onClose, onDone, user }) {
  const [type, setType] = useState(event._isSocietyWork ? "expense" : "income"); // Society Work = expense only
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function handleSubmit() {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) { setErr("Enter a valid amount."); return; }
    if (!description.trim()) { setErr("Please add a short description."); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, "eventEntries"), {
        eventId: event.docId,
        type, // "income" or "expense"
        amount: Number(amount),
        description: description.trim(),
        date,
        addedBy: user.name,
        addedAt: serverTimestamp(),
      });
      onDone();
    } catch (e) {
      console.error(e);
      setErr("Failed to save entry. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title={`Add Entry — ${event.title}`} onClose={onClose}>
      {/* Income/Expense toggle — hidden for Society Work (income comes via Gmail sync) */}
      {!event._isSocietyWork && (
        <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
          {["income", "expense"].map(t => (
            <button key={t} onClick={() => setType(t)} style={{
              flex: 1, padding: "10px", borderRadius: "10px", border: "1px solid",
              borderColor: type === t ? "#6c63ff" : "#2e2e40",
              background: type === t ? "#6c63ff20" : "transparent",
              color: type === t ? "#6c63ff" : "#8888a8",
              fontSize: "13px", fontWeight: "700", cursor: "pointer",
            }}>
              {t === "income" ? "💰 Income" : "📤 Expense"}
            </button>
          ))}
        </div>
      )}

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Amount (₹)</label>
        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" style={inputStyle} />
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Description</label>
        <input
          type="text" value={description} onChange={e => setDescription(e.target.value)}
          placeholder={type === "income" ? "e.g. Collected from Flat 101" : "e.g. Paid to vendor Indra"}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: "20px" }}>
        <label style={labelStyle}>Date</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
      </div>

      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}

      <button onClick={handleSubmit} disabled={saving} style={{
        width: "100%", padding: "14px", background: saving ? "#2e2e40" : "#6c63ff",
        border: "none", borderRadius: "12px", color: "#fff",
        fontSize: "15px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
      }}>
        {saving ? "Saving…" : `Add ${type === "income" ? "Income" : "Expense"}`}
      </button>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════
// ADD SECOND ASSIGNEE — Button + inline picker for President
// ════════════════════════════════════════════════════════════════
function AddSecondAssigneeButton({ event, onChanged }) {
  const [open, setOpen] = useState(false);
  const [peopleList, setPeopleList] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");
  const [saving, setSaving] = useState(false);

  async function loadPeople() {
    const snap = await getDocs(collection(db, "users"));
    const people = snap.docs
      .map(d => ({ uid: d.id, ...d.data() }))
      .filter(p => p.active !== false && p.role !== "super admin" && p.uid !== event.assignedToUid)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    setPeopleList(people);
  }

  async function handleSave() {
    if (!selectedUid) return;
    const person = peopleList.find(p => p.uid === selectedUid);
    if (!person) return;
    setSaving(true);
    try {
      const collName = event.category === "Society Work" ? "adhocEvents" : "events";
      await updateDoc(doc(db, collName, event.docId), {
        assignedToUid2:  selectedUid,
        assignedToName2: person.name,
      });
      await writeNotification({
        toUid:   selectedUid,
        message: `You've been added as second assignee for: "${event.title}"`,
        type:    "event_assigned",
      });
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); loadPeople(); }}
        style={{
          fontSize: "11px", padding: "4px 10px", borderRadius: "8px", cursor: "pointer",
          background: "#ffa72618", border: "1px solid #ffa72640", color: "#ffa726",
          fontWeight: "600",
        }}
      >
        ＋ Add Second Assignee
      </button>
    );
  }

  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
      <select
        value={selectedUid}
        onChange={e => setSelectedUid(e.target.value)}
        style={{ fontSize: "12px", padding: "4px 8px", borderRadius: "8px", background: "#0f0f1a", border: "1px solid #2e2e40", color: "#e8e8f0" }}
      >
        <option value="">-- Select Person 2 --</option>
        {peopleList.map(p => (
          <option key={p.uid} value={p.uid}>{p.name} {p.role ? `· ${p.role}` : ""}</option>
        ))}
      </select>
      <button onClick={handleSave} disabled={saving || !selectedUid} style={{
        fontSize: "11px", padding: "4px 10px", borderRadius: "8px", cursor: "pointer",
        background: "#4caf50", border: "none", color: "#fff", fontWeight: "600",
      }}>
        {saving ? "Saving…" : "Save"}
      </button>
      <button onClick={() => setOpen(false)} style={{
        fontSize: "11px", padding: "4px 8px", borderRadius: "8px", cursor: "pointer",
        background: "transparent", border: "1px solid #2e2e40", color: "#8888a8",
      }}>
        Cancel
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// NEW — Event Detail Screen (opens when an Event card is tapped)
// ════════════════════════════════════════════════════════════════
function EventDetailScreen({ event, onClose, onChanged, user, isMC }) {
  const { isPresident } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showVetConfirm, setShowVetConfirm] = useState(false);
  const [showEditDueDate, setShowEditDueDate] = useState(false);
  const [busy, setBusy] = useState(false);

  const userUid = user?.id || user?.uid;
  const isAssigned = userUid === event.assignedToUid || userUid === event.assignedToUid2;
  const isCreator  = user?.name === event.createdBy;
  const canEdit    = isAssigned || isCreator || isPresident();

  const [adhocCollected, setAdhocCollected] = useState(0);
  const [adhocEntries, setAdhocEntries] = useState([]);
  const [showFlatCollections, setShowFlatCollections] = useState(false);
  const [showEntries, setShowEntries] = useState(false);

  // Updates + Queries state
  const [updates, setUpdates] = useState([]);
  const [queries, setQueries] = useState([]);
  const [showUpdates, setShowUpdates] = useState(false);
  const [showQueries, setShowQueries] = useState(false);
  const [newUpdate, setNewUpdate] = useState("");
  const [newQuery, setNewQuery] = useState("");
  const [replyText, setReplyText] = useState({});
  const [postingUpdate, setPostingUpdate] = useState(false);
  const [postingQuery, setPostingQuery] = useState(false);
  const [postingReply, setPostingReply] = useState({});

  async function loadEntries() {
    setLoading(true);
    try {
      // Always fetch manual eventEntries
      const snap = await getDocs(
        query(collection(db, "eventEntries"), where("eventId", "==", event.docId))
      );
      const list = snap.docs.map(d => ({ docId: d.id, ...d.data() }));
      list.sort((a, b) => (tsToDate(b.addedAt) || 0) - (tsToDate(a.addedAt) || 0));
      setEntries(list);

      // For Society Work events, also fetch paid adhocPayments (flat-by-flat collections)
      if (event._isSocietyWork) {
        const aSnap = await getDocs(
          query(collection(db, "adhocPayments"),
            where("eventId", "==", event.docId),
            where("paid", "==", true))
        );
        const paid = aSnap.docs.map(d => ({ docId: d.id, ...d.data() }));
        const total = paid.reduce((s, a) => s + Number(a.paidAmount || a.amount || 0), 0);
        setAdhocCollected(total);
        // Build display entries for each paid flat
        const flatEntries = paid.map(a => ({
          docId: a.docId,
          type: "income",
          description: `Collected from Flat ${a.flatId}`,
          amount: a.paidAmount || a.amount,
          date: a.paidDate || "",
          addedBy: a.paidBy || "Flat owner",
          _isAdhoc: true,
        }));
        flatEntries.sort((a, b) => (a.date > b.date ? -1 : 1));
        setAdhocEntries(flatEntries);
      } else {
        setAdhocCollected(0);
        setAdhocEntries([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function loadUpdatesAndQueries() {
    try {
      const collName = event._isSocietyWork ? "adhocEvents" : "events";
      const [uSnap, qSnap] = await Promise.all([
        getDocs(query(collection(db, "eventUpdates"), where("eventId", "==", event.docId))),
        getDocs(query(collection(db, "eventQueries"), where("eventId", "==", event.docId))),
      ]);
      const uList = uSnap.docs.map(d => ({ docId: d.id, ...d.data() }));
      uList.sort((a, b) => (tsToDate(b.postedAt) || 0) - (tsToDate(a.postedAt) || 0));
      setUpdates(uList);

      const qList = qSnap.docs.map(d => ({ docId: d.id, ...d.data() }));
      qList.sort((a, b) => (tsToDate(b.postedAt) || 0) - (tsToDate(a.postedAt) || 0));
      setQueries(qList);
    } catch (e) {
      console.error("loadUpdatesAndQueries error:", e);
    }
  }

  useEffect(() => { loadEntries(); loadUpdatesAndQueries(); }, [event.docId]);

  const manualIncome  = entries.filter(e => e.type === "income").reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalIncome   = manualIncome + adhocCollected;
  const totalExpense  = entries.filter(e => e.type === "expense").reduce((s, e) => s + Number(e.amount || 0), 0);
  const balance       = totalIncome - totalExpense;

  // Step 1: assigned person requests closure
  async function handleRequestClose() {
    setBusy(true);
    try {
      await updateDoc(doc(db, "events", event.docId), { status: "pending_close" });
      setShowCloseConfirm(false);
      onChanged();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }

  // Step 2: any MC member vets and confirms closure
  async function handleVetConfirm() {
    setBusy(true);
    try {
      const note = balance > 0
        ? `Event closed with ${fmt(balance)} surplus on ${todayDMY()}`
        : balance < 0
          ? `Event closed with ${fmt(Math.abs(balance))} deficit on ${todayDMY()}`
          : `Event closed and settled exactly on ${todayDMY()}`;

      await updateDoc(doc(db, "events", event.docId), {
        status: "closed",
        closedAt: serverTimestamp(),
        closedByUid: user.id || user.uid,
        closedByName: user.name,
        closeNote: note,
      });

      // Surplus merges into main maintenance balance — deficit does NOT auto-deduct.
      if (balance > 0) {
        const settingsRef = doc(db, "settings", "init");
        const settingsSnap = await getDoc(settingsRef);
        const currentBalance = settingsSnap.exists() ? Number(settingsSnap.data().appBalance || 0) : 0;
        await updateDoc(settingsRef, { appBalance: currentBalance + balance });
      }

      setShowVetConfirm(false);
      onChanged();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }

  async function handlePostUpdate() {
    if (!newUpdate.trim()) return;
    setPostingUpdate(true);
    try {
      await addDoc(collection(db, "eventUpdates"), {
        eventId:    event.docId,
        eventTitle: event.title,
        message:    newUpdate.trim(),
        postedBy:   user.name,
        postedByUid: user.id || user.uid,
        postedAt:   serverTimestamp(),
      });
      await writeNotification({
        toUid:   "all",
        message: `📢 Update on "${event.title}": ${newUpdate.trim()}`,
        type:    "event_update",
      });
      setNewUpdate("");
      await loadUpdatesAndQueries();
    } catch (e) { console.error(e); }
    finally { setPostingUpdate(false); }
  }

  async function handlePostQuery() {
    if (!newQuery.trim()) return;
    setPostingQuery(true);
    try {
      await addDoc(collection(db, "eventQueries"), {
        eventId:    event.docId,
        eventTitle: event.title,
        question:   newQuery.trim(),
        postedBy:   user.name,
        postedByUid: user.id || user.uid,
        postedAt:   serverTimestamp(),
        replies:    [],
      });
      await writeNotification({
        toUid:   "all",
        message: `❓ New query on "${event.title}" by ${user.name}: ${newQuery.trim()}`,
        type:    "event_query",
      });
      setNewQuery("");
      await loadUpdatesAndQueries();
    } catch (e) { console.error(e); }
    finally { setPostingQuery(false); }
  }

  async function handlePostReply(queryDocId, question) {
    const text = replyText[queryDocId]?.trim();
    if (!text) return;
    setPostingReply(p => ({ ...p, [queryDocId]: true }));
    try {
      const reply = { by: user.name, byUid: user.id || user.uid, text, at: new Date().toISOString() };
      const qRef = doc(db, "eventQueries", queryDocId);
      const qSnap = await getDoc(qRef);
      const existing = qSnap.data().replies || [];
      await updateDoc(qRef, { replies: [...existing, reply] });
      await writeNotification({
        toUid:   "all",
        message: `💬 Reply on "${event.title}" query "${question}": ${text}`,
        type:    "event_reply",
      });
      setReplyText(p => ({ ...p, [queryDocId]: "" }));
      await loadUpdatesAndQueries();
    } catch (e) { console.error(e); }
    finally { setPostingReply(p => ({ ...p, [queryDocId]: false })); }
  }

  const canPostUpdate = isMC || isAssigned;
  const canReply      = isMC || isAssigned;

  return (
    <ModalShell title={event.title} onClose={onClose} maxHeight="95vh">
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "11px", padding: "4px 10px", borderRadius: "8px", background: "#6c63ff18", border: "1px solid #6c63ff40", color: "#6c63ff", fontWeight: "600" }}>
          Assigned to {event.assignedToName}{event.assignedToName2 ? ` & ${event.assignedToName2}` : ""}
        </span>
        {/* Add Second Assignee — President only, when Person 2 is missing and event is open */}
        {isPresident() && !event.assignedToName2 && event.status === "open" && (
          <AddSecondAssigneeButton event={event} onChanged={onChanged} />
        )}
        <span style={{
          fontSize: "11px", padding: "4px 10px", borderRadius: "8px", fontWeight: "600",
          background: event.status === "closed" ? "#4caf5018" : event.status === "pending_close" ? "#ffa72618" : "#42a5f518",
          border: `1px solid ${event.status === "closed" ? "#4caf5040" : event.status === "pending_close" ? "#ffa72640" : "#42a5f540"}`,
          color: event.status === "closed" ? "#4caf50" : event.status === "pending_close" ? "#ffa726" : "#42a5f5",
        }}>
          {event.status === "closed" ? "Closed" : event.status === "pending_close" ? "Pending MC confirmation" : "In progress"}
        </span>
      </div>

      {/* Start Date + Expected Closure + Budget row */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <div style={{ flex: 1, background: "#0f0f13", borderRadius: "10px", padding: "10px" }}>
          <div style={{ fontSize: "10px", color: "#8888a8", marginBottom: "4px" }}>Start Date</div>
          <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>
            {event.startDate
              ? new Date(event.startDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
              : "—"}
          </div>
        </div>
        <div style={{ flex: 1, background: "#0f0f13", borderRadius: "10px", padding: "10px" }}>
          <div style={{ fontSize: "10px", color: "#8888a8", marginBottom: "4px" }}>Expected Closure</div>
          <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>
            {event.dueDate
              ? new Date(event.dueDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
              : "—"}
          </div>
          {canEdit && event.status === "open" && (
            <button
              onClick={() => setShowEditDueDate(true)}
              style={{ marginTop: "6px", background: "none", border: "none", color: "#6c63ff", fontSize: "11px", fontWeight: "600", cursor: "pointer", padding: 0 }}
            >
              ✏ Edit
            </button>
          )}
        </div>
        {event.estimatedBudget != null && (
          <div style={{ flex: 1, background: "#0f0f13", borderRadius: "10px", padding: "10px" }}>
            <div style={{ fontSize: "10px", color: "#8888a8", marginBottom: "4px" }}>Est. Budget</div>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#ffa726" }}>
              {fmt(event.estimatedBudget)}
            </div>
          </div>
        )}
      </div>

      {/* Balance summary */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <div style={{ flex: 1, background: "#0f0f13", borderRadius: "10px", padding: "10px", textAlign: "center" }}>
          <div style={{ fontSize: "10px", color: "#8888a8" }}>Collected</div>
          <div style={{ fontSize: "15px", fontWeight: "700", color: "#4caf50" }}>{fmt(totalIncome)}</div>
        </div>
        <div style={{ flex: 1, background: "#0f0f13", borderRadius: "10px", padding: "10px", textAlign: "center" }}>
          <div style={{ fontSize: "10px", color: "#8888a8" }}>Spent</div>
          <div style={{ fontSize: "15px", fontWeight: "700", color: "#ff5252" }}>{fmt(totalExpense)}</div>
        </div>
        <div style={{ flex: 1, background: "#0f0f13", borderRadius: "10px", padding: "10px", textAlign: "center" }}>
          <div style={{ fontSize: "10px", color: "#8888a8" }}>Balance</div>
          <div style={{ fontSize: "15px", fontWeight: "700", color: balance >= 0 ? "#4caf50" : "#ff5252" }}>{fmt(balance)}</div>
        </div>
      </div>

      {event.closeNote && (
        <div style={{ background: "#4caf5010", border: "1px solid #4caf5030", borderRadius: "10px", padding: "10px 12px", marginBottom: "16px", color: "#4caf50", fontSize: "12px" }}>
          ✓ {event.closeNote}
        </div>
      )}

      {/* Society Work — two collapsible columns side by side */}
      {adhocEntries.length > 0 ? (
        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>

          {/* Left column — Flat Collections (income) */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              onClick={() => setShowFlatCollections(p => !p)}
              style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: "#0d1f0d", border: "1px solid #2e4d2e", borderRadius: "8px",
                padding: "8px 10px", cursor: "pointer", userSelect: "none", marginBottom: "6px",
              }}
            >
              <div style={{ fontSize: "11px", color: "#4caf50", fontWeight: "600" }}>
                🏠 Income ({adhocEntries.length})
              </div>
              <div style={{ fontSize: "12px", color: "#4caf50", fontWeight: "700" }}>
                {showFlatCollections ? "▲" : "▼"}
              </div>
            </div>
            {showFlatCollections && adhocEntries.map(e => (
              <div key={e.docId} style={{
                background: "#111118", border: "1px solid #2e2e40", borderRadius: "6px",
                padding: "5px 8px", marginBottom: "4px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: "11px", color: "#e8e8f0", fontWeight: "600" }}>
                    {e.description.replace("Collected from Flat ", "Flat ")}
                  </div>
                  <div style={{ fontSize: "10px", color: "#555570" }}>{e.date}</div>
                </div>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#4caf50" }}>+{fmt(e.amount)}</div>
              </div>
            ))}
          </div>

          {/* Right column — Expenses */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              onClick={() => setShowEntries(p => !p)}
              style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: "#1f0d0d", border: "1px solid #4d2e2e", borderRadius: "8px",
                padding: "8px 10px", cursor: "pointer", userSelect: "none", marginBottom: "6px",
              }}
            >
              <div style={{ fontSize: "11px", color: "#ff5252", fontWeight: "600" }}>
                💸 Expenses ({entries.length})
              </div>
              <div style={{ fontSize: "12px", color: "#ff5252", fontWeight: "700" }}>
                {showEntries ? "▲" : "▼"}
              </div>
            </div>
            {showEntries && (
              loading ? (
                <div style={{ fontSize: "11px", color: "#555570", padding: "8px 0" }}>Loading…</div>
              ) : entries.length === 0 ? (
                <div style={{ fontSize: "11px", color: "#555570", padding: "8px 0", textAlign: "center" }}>No expenses yet.</div>
              ) : (
                entries.map(e => (
                  <div key={e.docId} style={{
                    background: "#111118", border: "1px solid #2e2e40", borderRadius: "6px",
                    padding: "5px 8px", marginBottom: "4px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <div>
                      <div style={{ fontSize: "11px", color: "#e8e8f0", fontWeight: "600" }}>{e.description}</div>
                      <div style={{ fontSize: "10px", color: "#555570" }}>{e.date}</div>
                    </div>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: e.type === "income" ? "#4caf50" : "#ff5252" }}>
                      {e.type === "income" ? "+" : "−"}{fmt(e.amount)}
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        </div>
      ) : (
        /* General Event — entries full width */
        <>
          <div style={{ fontSize: "11px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600", marginBottom: "8px" }}>
            Entries
          </div>
          {loading ? (
            <div style={{ textAlign: "center", padding: "20px", color: "#555570", fontSize: "13px" }}>Loading…</div>
          ) : entries.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px", color: "#555570", fontSize: "13px" }}>No entries yet.</div>
          ) : (
            entries.map(e => (
              <div key={e.docId} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "10px",
                padding: "10px 12px", marginBottom: "8px",
              }}>
                <div>
                  <div style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "600" }}>{e.description}</div>
                  <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
                    {e.date} · Added by {e.addedBy}
                  </div>
                </div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: e.type === "income" ? "#4caf50" : "#ff5252" }}>
                  {e.type === "income" ? "+" : "−"}{fmt(e.amount)}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* Add entry — assigned person only, while event still open */}
      {isAssigned && event.status === "open" && (
        <button onClick={() => setShowAddEntry(true)} style={{
          width: "100%", marginTop: "12px", padding: "12px", borderRadius: "12px",
          background: "#1a1a24", border: "1px dashed #2e2e40",
          color: "#6c63ff", fontSize: "13px", fontWeight: "600", cursor: "pointer",
        }}>
          + Add Income / Expense Entry
        </button>
      )}

      {/* ── Updates Section ── */}
      <div style={{ marginTop: "16px" }}>
        <div
          onClick={() => setShowUpdates(p => !p)}
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "#0d1628", border: "1px solid #1e3a5f", borderRadius: "8px",
            padding: "8px 10px", cursor: "pointer", userSelect: "none", marginBottom: "6px",
          }}
        >
          <div style={{ fontSize: "11px", color: "#42a5f5", fontWeight: "600" }}>
            📢 Updates ({updates.length})
          </div>
          <div style={{ fontSize: "12px", color: "#42a5f5", fontWeight: "700" }}>
            {showUpdates ? "▲" : "▼"}
          </div>
        </div>
        {showUpdates && (
          <div>
            {updates.length === 0 && (
              <div style={{ fontSize: "11px", color: "#555570", padding: "6px 0", textAlign: "center" }}>No updates yet.</div>
            )}
            {updates.map(u => (
              <div key={u.docId} style={{
                background: "#111118", border: "1px solid #1e3a5f", borderRadius: "6px",
                padding: "8px 10px", marginBottom: "6px",
              }}>
                <div style={{ fontSize: "12px", color: "#e8e8f0" }}>{u.message}</div>
                <div style={{ fontSize: "10px", color: "#555570", marginTop: "4px" }}>
                  — {u.postedBy} · {u.postedAt?.toDate ? u.postedAt.toDate().toLocaleDateString("en-IN") : ""}
                </div>
              </div>
            ))}
            {canPostUpdate && (
              <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                <input
                  value={newUpdate}
                  onChange={e => setNewUpdate(e.target.value)}
                  placeholder="Post an update…"
                  style={{ flex: 1, fontSize: "12px", padding: "7px 10px", borderRadius: "8px", background: "#0f0f1a", border: "1px solid #2e2e40", color: "#e8e8f0" }}
                />
                <button onClick={handlePostUpdate} disabled={postingUpdate || !newUpdate.trim()} style={{
                  fontSize: "11px", padding: "7px 12px", borderRadius: "8px", cursor: "pointer",
                  background: "#42a5f5", border: "none", color: "#fff", fontWeight: "600",
                }}>
                  {postingUpdate ? "…" : "Post"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Queries Section ── */}
      <div style={{ marginTop: "10px" }}>
        <div
          onClick={() => setShowQueries(p => !p)}
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "#1a1028", border: "1px solid #3d2e5f", borderRadius: "8px",
            padding: "8px 10px", cursor: "pointer", userSelect: "none", marginBottom: "6px",
          }}
        >
          <div style={{ fontSize: "11px", color: "#ab47bc", fontWeight: "600" }}>
            ❓ Queries ({queries.length})
          </div>
          <div style={{ fontSize: "12px", color: "#ab47bc", fontWeight: "700" }}>
            {showQueries ? "▲" : "▼"}
          </div>
        </div>
        {showQueries && (
          <div>
            {queries.length === 0 && (
              <div style={{ fontSize: "11px", color: "#555570", padding: "6px 0", textAlign: "center" }}>No queries yet.</div>
            )}
            {queries.map(q => (
              <div key={q.docId} style={{
                background: "#111118", border: "1px solid #3d2e5f", borderRadius: "6px",
                padding: "8px 10px", marginBottom: "8px",
              }}>
                <div style={{ fontSize: "12px", color: "#e8e8f0", fontWeight: "600" }}>{q.question}</div>
                <div style={{ fontSize: "10px", color: "#555570", marginTop: "2px" }}>
                  — {q.postedBy} · {q.postedAt?.toDate ? q.postedAt.toDate().toLocaleDateString("en-IN") : ""}
                </div>
                {/* Replies */}
                {(q.replies || []).map((r, i) => (
                  <div key={i} style={{
                    background: "#0f0f1a", borderLeft: "2px solid #ab47bc",
                    padding: "5px 8px", marginTop: "6px", borderRadius: "0 6px 6px 0",
                  }}>
                    <div style={{ fontSize: "11px", color: "#e8e8f0" }}>{r.text}</div>
                    <div style={{ fontSize: "10px", color: "#555570", marginTop: "2px" }}>— {r.by}</div>
                  </div>
                ))}
                {/* Reply input — MC + Assignee only */}
                {canReply && (
                  <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                    <input
                      value={replyText[q.docId] || ""}
                      onChange={e => setReplyText(p => ({ ...p, [q.docId]: e.target.value }))}
                      placeholder="Reply…"
                      style={{ flex: 1, fontSize: "11px", padding: "5px 8px", borderRadius: "6px", background: "#0f0f1a", border: "1px solid #2e2e40", color: "#e8e8f0" }}
                    />
                    <button
                      onClick={() => handlePostReply(q.docId, q.question)}
                      disabled={postingReply[q.docId] || !replyText[q.docId]?.trim()}
                      style={{
                        fontSize: "11px", padding: "5px 10px", borderRadius: "6px", cursor: "pointer",
                        background: "#ab47bc", border: "none", color: "#fff", fontWeight: "600",
                      }}
                    >
                      {postingReply[q.docId] ? "…" : "Reply"}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {/* Post new query — everyone */}
            <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
              <input
                value={newQuery}
                onChange={e => setNewQuery(e.target.value)}
                placeholder="Ask a question…"
                style={{ flex: 1, fontSize: "12px", padding: "7px 10px", borderRadius: "8px", background: "#0f0f1a", border: "1px solid #2e2e40", color: "#e8e8f0" }}
              />
              <button onClick={handlePostQuery} disabled={postingQuery || !newQuery.trim()} style={{
                fontSize: "11px", padding: "7px 12px", borderRadius: "8px", cursor: "pointer",
                background: "#ab47bc", border: "none", color: "#fff", fontWeight: "600",
              }}>
                {postingQuery ? "…" : "Ask"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Close request — assigned person only, while open */}
      {isAssigned && event.status === "open" && (
        <button onClick={() => setShowCloseConfirm(true)} style={{
          width: "100%", marginTop: "10px", padding: "12px", borderRadius: "12px",
          background: "#ffa72618", border: "1px solid #ffa72640",
          color: "#ffa726", fontSize: "13px", fontWeight: "700", cursor: "pointer",
        }}>
          Close Event
        </button>
      )}

      {/* MC vetting — any MC member, while pending_close */}
      {isMC && event.status === "pending_close" && (
        <button onClick={() => setShowVetConfirm(true)} style={{
          width: "100%", marginTop: "10px", padding: "12px", borderRadius: "12px",
          background: "#4caf5018", border: "1px solid #4caf5040",
          color: "#4caf50", fontSize: "13px", fontWeight: "700", cursor: "pointer",
        }}>
          Confirm Closure (MC)
        </button>
      )}

      {showAddEntry && (
        <AddEventEntryModal
          event={event}
          user={user}
          onClose={() => setShowAddEntry(false)}
          onDone={() => { setShowAddEntry(false); loadEntries(); }}
        />
      )}

      {showEditDueDate && (
        <EditDueDateModal
          event={event}
          user={user}
          onClose={() => setShowEditDueDate(false)}
          onDone={() => { setShowEditDueDate(false); onChanged(); }}
        />
      )}

      {showCloseConfirm && (
        <ModalShell title="Close this event?" onClose={() => setShowCloseConfirm(false)}>
          <div style={{ fontSize: "13px", color: "#e8e8f0", marginBottom: "16px", lineHeight: 1.5 }}>
            Current balance is <strong>{fmt(balance)}</strong>. Once you close, one MC member will need to confirm before it's final.
          </div>
          <button onClick={handleRequestClose} disabled={busy} style={{
            width: "100%", padding: "14px", background: busy ? "#2e2e40" : "#ffa726",
            border: "none", borderRadius: "12px", color: "#fff",
            fontSize: "15px", fontWeight: "700", cursor: busy ? "not-allowed" : "pointer",
          }}>
            {busy ? "Submitting…" : "Yes, request closure"}
          </button>
        </ModalShell>
      )}

      {showVetConfirm && (
        <ModalShell title="Confirm event closure" onClose={() => setShowVetConfirm(false)}>
          <div style={{ fontSize: "13px", color: "#e8e8f0", marginBottom: "16px", lineHeight: 1.5 }}>
            {balance > 0 && <>This will add <strong>{fmt(balance)}</strong> to the main maintenance balance as a surplus.</>}
            {balance < 0 && <>This event has a deficit of <strong>{fmt(Math.abs(balance))}</strong>. It will be recorded as-is — nothing will be deducted from maintenance.</>}
            {balance === 0 && <>This event is exactly settled — nothing to merge.</>}
          </div>
          <button onClick={handleVetConfirm} disabled={busy} style={{
            width: "100%", padding: "14px", background: busy ? "#2e2e40" : "#4caf50",
            border: "none", borderRadius: "12px", color: "#fff",
            fontSize: "15px", fontWeight: "700", cursor: busy ? "not-allowed" : "pointer",
          }}>
            {busy ? "Confirming…" : "Confirm & Close"}
          </button>
        </ModalShell>
      )}
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════
// NEW — Event Card (shown in the Events filter list)
// ════════════════════════════════════════════════════════════════
function EventCard({ event, entries, adhocTotals, onClick, canManage, onDeleted, user }) {
  const myEntries = entries.filter(e => e.eventId === event.docId);
  const manualIncome  = myEntries.filter(e => e.type === "income").reduce((s, e) => s + Number(e.amount || 0), 0);
  const adhocIncome   = (adhocTotals && adhocTotals[event.docId]) || 0;
  const totalIncome   = manualIncome + adhocIncome;
  const totalExpense  = myEntries.filter(e => e.type === "expense").reduce((s, e) => s + Number(e.amount || 0), 0);
  const balance = totalIncome - totalExpense;
  const [deleting, setDeleting] = useState(false);

  // FIX — TasksScreen 3.3 (design frozen 2026-07-13):
  // "Delete" no longer removes the event or any of its dues from
  // Firestore. It only marks the event disabled=true. Every due
  // record it created (paid or unpaid) stays exactly as it was —
  // nothing is ever deleted or modified. A disabled event just
  // stops showing in this list, and its dues stop counting toward
  // App Balance / income totals (see computeLiveAppBalance in
  // PassbookScreen.jsx and the adhocTotals calc below).
  //
  // REFUND WORKFLOW — added 2026-07-13 (scope expansion, design frozen
  // same day): if this is a Society Work event, any flat that had
  // already PAID gets a brand-new second adhocPayments document marking
  // a refund as owed (reversalStatus: "pending"). The original "Paid"
  // document is never touched — full history stays exactly as it was.
  // Flats that hadn't paid need no refund; their due just stops
  // counting, nothing further happens. The new refund doc is created
  // with paid left false/unset so it is never mistaken for real income
  // in computeLiveAppBalance. Both the event-disable and the refund-doc
  // creation happen in ONE atomic batch, so either everything is
  // recorded together or nothing is — same pattern as TasksScreen 3.1.
  async function handleDelete(e) {
    e.stopPropagation();
    const confirmMsg = event._isSocietyWork
      ? `Disable "${event.title}"? It will no longer show here or count toward totals. Any flat that already paid will get a "Refund Pending" entry created for the Treasurer to process. Nothing is deleted — flat owners will still see their own paid/unpaid history for it.`
      : `Disable "${event.title}"? It will no longer show here or count toward totals. Nothing is deleted — flat owners will still see their own paid/unpaid history for it.`;
    if (!window.confirm(confirmMsg)) return;
    setDeleting(true);
    try {
      const collName = event._isSocietyWork ? "adhocEvents" : "events";
      const batch = writeBatch(db);

      batch.update(doc(db, collName, event.docId), {
        disabled:   true,
        disabledBy: user?.name || "",
        disabledAt: serverTimestamp(),
      });

      // Only Society Work events use adhocPayments — create refund
      // records for any flat that already paid.
      if (event._isSocietyWork) {
        const paidSnap = await getDocs(query(
          collection(db, "adhocPayments"),
          where("eventId", "==", event.docId),
          where("paid", "==", true)
        ));
        paidSnap.docs.forEach((d) => {
          const orig = d.data();
          const refundRef = doc(collection(db, "adhocPayments"));
          batch.set(refundRef, {
            flatId:            orig.flatId,
            eventId:           orig.eventId,
            eventTitle:        orig.eventTitle || event.title,
            amount:            orig.paidAmount ?? orig.amount ?? 0,
            societyId:         orig.societyId,
            originalPaymentId: d.id,
            reversalStatus:    "pending",
            reversalApprovedBy: "",
            reversalDate:       "",
            paid:              false,
            createdAt:         serverTimestamp(),
            createdBy:         user?.name || "",
          });
        });
      }

      await batch.commit();
      onDeleted();
    } catch (err) {
      console.error(err);
      alert("Failed to disable. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  const statusInfo = event.status === "closed"
    ? { label: "Closed", color: "#4caf50", bg: "#4caf5018", border: "#4caf5040" }
    : event.status === "pending_close"
      ? { label: "Pending MC confirmation", color: "#ffa726", bg: "#ffa72618", border: "#ffa72640" }
      : { label: "In progress", color: "#42a5f5", bg: "#42a5f518", border: "#42a5f540" };

  return (
    <div onClick={onClick} style={{
      background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "14px",
      padding: "14px", marginBottom: "10px", cursor: "pointer",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
        <div>
          {event._isSocietyWork && (
            <div style={{ fontSize: "10px", color: "#ffa726", fontWeight: "600", marginBottom: "3px" }}>
              🏗️ Society Work
            </div>
          )}
          <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>{event.title}</div>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          {event.disabled ? (
            <span
              title="This task is disabled."
              style={{
                fontSize: "10px", padding: "2px 8px", borderRadius: "6px", fontWeight: "600",
                background: "#88888818", border: "1px solid #88888840", color: "#aaaaaa",
              }}
            >
              Disabled
            </span>
          ) : (
            <span style={{
              fontSize: "10px", padding: "2px 8px", borderRadius: "6px", fontWeight: "600",
              background: statusInfo.bg, border: `1px solid ${statusInfo.border}`, color: statusInfo.color,
            }}>
              {statusInfo.label}
            </span>
          )}
          {canManage && !event.disabled && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              title="This will disable the task."
              style={{
                background: "none", border: "1px solid #ff525240", borderRadius: "6px",
                color: "#ff5252", fontSize: "10px", fontWeight: "600", cursor: "pointer",
                padding: "3px 8px", lineHeight: 1,
              }}
            >
              Disable
            </button>
          )}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
        <div style={{ fontSize: "11px", color: "#555570" }}>
          Assigned to {event.assignedToName}{event.assignedToName2 ? ` & ${event.assignedToName2}` : ""}
        </div>
        {event.dueDate && (
          <div style={{ fontSize: "11px", color: "#8888a8" }}>
            Due: {new Date(event.dueDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
          </div>
        )}
      </div>
      {event.status === "open" && event.createdAt && (
        <div style={{ fontSize: "10px", color: "#ffa726", marginBottom: "6px", fontWeight: "600" }}>
          ⏱ {agingLabel(event.createdAt, event.startDate)}
        </div>
      )}
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
        <div style={{ fontSize: "12px", color: "#4caf50" }}>+{fmt(totalIncome)}</div>
        <div style={{ fontSize: "12px", color: "#ff5252" }}>−{fmt(totalExpense)}</div>
        <div style={{ fontSize: "12px", color: "#e8e8f0", fontWeight: "700", marginLeft: "auto" }}>
          Balance: {fmt(balance)}
        </div>
        {event.estimatedBudget != null && (
          <div style={{ fontSize: "11px", color: "#ffa726", width: "100%" }}>
            Budget: {fmt(event.estimatedBudget)}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// NEW — Add to Maintenance Income Modal (Treasurer only)
// Adds a lump-sum income directly into appBalance in settings/init
// ════════════════════════════════════════════════════════════════
function AddMaintenanceIncomeModal({ onClose, onDone, user }) {
  const [description, setDescription] = useState("");
  const [type, setType]               = useState("");
  const [amount, setAmount]           = useState("");
  const [date, setDate]               = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState("");

  async function handleSubmit() {
    if (!description.trim()) { setErr("Please enter a description."); return; }
    if (!type.trim()) { setErr("Please enter a type (e.g. Bank Interest, Scrap Sale)."); return; }
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) { setErr("Enter a valid amount."); return; }
    if (!date) { setErr("Please select a date."); return; }
    setSaving(true);
    try {
      // Derive month string like "2026-06" from the selected date
      const d = new Date(date);
      const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

      await addDoc(collection(db, "maintenanceIncome"), {
        type,
        amount:      Number(amount),
        description: description.trim(),
        date,
        month:       monthStr,
        addedBy:     user.name,
        addedAt:     serverTimestamp(),
      });

      onDone();
    } catch (e) {
      console.error(e);
      setErr("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Add to Maintenance" onClose={onClose}>
      <div style={{ background: "#4caf5010", border: "1px solid #4caf5030", borderRadius: "10px", padding: "10px 12px", marginBottom: "16px", fontSize: "12px", color: "#4caf50" }}>
        💡 This amount will be added directly to the main society balance.
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Description *</label>
        <input
          type="text" value={description} onChange={e => setDescription(e.target.value)}
          placeholder="e.g. 2024-25 closing balance carried forward"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Type *</label>
        <input
          type="text" value={type} onChange={e => setType(e.target.value)}
          placeholder="e.g. Bank Interest, Scrap Sale, Donation…"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Amount (₹) *</label>
        <input
          type="number" value={amount} onChange={e => setAmount(e.target.value)}
          placeholder="0"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: "20px" }}>
        <label style={labelStyle}>Date Received *</label>
        <input
          type="date" value={date} onChange={e => setDate(e.target.value)}
          style={inputStyle}
        />
      </div>

      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}

      <button onClick={handleSubmit} disabled={saving} style={{
        width: "100%", padding: "14px", background: saving ? "#2e2e40" : "#4caf50",
        border: "none", borderRadius: "12px", color: "#fff",
        fontSize: "15px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
      }}>
        {saving ? "Saving…" : "Add to Maintenance Balance"}
      </button>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════
// MEETING REMINDER HELPERS
// ════════════════════════════════════════════════════════════════

// Returns the date of the Nth occurrence of a weekday in a given month.
// weekday: 0=Sun, 1=Mon … 6=Sat
function getNthWeekdayOfMonth(year, month, weekday, nth) {
  const d = new Date(year, month, 1);
  let count = 0;
  while (d.getMonth() === month) {
    if (d.getDay() === weekday) {
      count++;
      if (count === nth) return new Date(d);
    }
    d.setDate(d.getDate() + 1);
  }
  return null;
}

// Returns true if today is the Saturday before the 3rd Sunday this month
function isSaturdayBeforeThirdSunday() {
  const now   = new Date();
  const year  = now.getFullYear();
  const month = now.getMonth();
  const thirdSunday = getNthWeekdayOfMonth(year, month, 0, 3);
  if (!thirdSunday) return false;
  const satBefore = new Date(thirdSunday);
  satBefore.setDate(thirdSunday.getDate() - 1);
  return (
    now.getFullYear() === satBefore.getFullYear() &&
    now.getMonth()    === satBefore.getMonth() &&
    now.getDate()     === satBefore.getDate()
  );
}

// Returns the next upcoming 3rd Sunday (this month or next)
function getNextThirdSunday() {
  const now   = new Date();
  for (let offset = 0; offset <= 1; offset++) {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const ts = getNthWeekdayOfMonth(d.getFullYear(), d.getMonth(), 0, 3);
    if (ts && ts >= now) return ts;
  }
  return null;
}

// ════════════════════════════════════════════════════════════════
// MEETING REMINDER BANNER + EDIT MODAL (President only)
// ════════════════════════════════════════════════════════════════
function MeetingReminderBanner({ user, isPresident }) {
  const [config, setConfig]         = useState(null); // from settings/init.meetingReminder
  const [showEdit, setShowEdit]     = useState(false);
  const [notifSent, setNotifSent]   = useState(false);

  const defaultConfig = { time: "11:00", message: "Association Meeting" };

  useEffect(() => {
    async function loadAndMaybeNotify() {
      try {
        const snap = await getDoc(doc(db, "settings", "init"));
        const data = snap.exists() ? snap.data() : {};
        const cfg  = data.meetingReminder || defaultConfig;
        setConfig(cfg);

        // Check if we should send reminder notification today (Saturday before 3rd Sunday)
        if (isSaturdayBeforeThirdSunday()) {
          const todayKey = new Date().toISOString().slice(0, 10);
          const lastSent = data.meetingReminderLastSent || "";
          if (lastSent !== todayKey) {
            const nextSunday = getNextThirdSunday();
            const dateStr = nextSunday
              ? nextSunday.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
              : "this Sunday";
            await writeNotification({
              toUid:   "all",
              message: `📅 Reminder: ${cfg.message} is tomorrow (${dateStr}) at ${cfg.time}. Please attend!`,
              type:    "meeting_reminder",
            });
            // Mark today as sent so it doesn't fire again this session
            await updateDoc(doc(db, "settings", "init"), { meetingReminderLastSent: todayKey });
            setNotifSent(true);
          }
        }
      } catch (e) {
        console.error(e);
        setConfig(defaultConfig);
      }
    }
    loadAndMaybeNotify();
  }, []);

  const nextSunday = getNextThirdSunday();
  const nextDateStr = nextSunday
    ? nextSunday.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  if (!config) return null;

  return (
    <>
      <div style={{
        background: "#6c63ff18", border: "1px solid #6c63ff40",
        borderRadius: "14px", padding: "12px 14px", marginBottom: "16px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#6c63ff" }}>
              📅 {config.message}
            </div>
            <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "4px" }}>
              Every 3rd Sunday · {config.time} · Next: {nextDateStr}
            </div>
            {notifSent && (
              <div style={{ fontSize: "11px", color: "#4caf50", marginTop: "4px" }}>
                ✓ Reminder sent to everyone today
              </div>
            )}
          </div>
          {isPresident && (
            <button
              onClick={() => setShowEdit(true)}
              style={{ background: "none", border: "none", color: "#6c63ff", fontSize: "11px", fontWeight: "600", cursor: "pointer", padding: 0 }}
            >
              ✏ Edit
            </button>
          )}
        </div>
      </div>

      {showEdit && (
        <EditMeetingReminderModal
          config={config}
          user={user}
          onClose={() => setShowEdit(false)}
          onDone={(updated) => { setConfig(updated); setShowEdit(false); }}
        />
      )}
    </>
  );
}

function EditMeetingReminderModal({ config, onClose, onDone }) {
  const [time, setTime]       = useState(config.time || "11:00");
  const [message, setMessage] = useState(config.message || "Association Meeting");
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState("");

  async function handleSave() {
    if (!message.trim()) { setErr("Meeting name is required."); return; }
    if (!time)           { setErr("Meeting time is required."); return; }
    setSaving(true);
    try {
      const updated = { time, message: message.trim() };
      await updateDoc(doc(db, "settings", "init"), { meetingReminder: updated });
      onDone(updated);
    } catch (e) {
      console.error(e);
      setErr("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Edit Meeting Reminder" onClose={onClose}>
      <div style={{ marginBottom: "14px" }}>
        <label style={labelStyle}>Meeting Name</label>
        <input
          type="text" value={message} onChange={e => setMessage(e.target.value)}
          placeholder="e.g. Association Meeting"
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: "20px" }}>
        <label style={labelStyle}>Time</label>
        <input
          type="time" value={time} onChange={e => setTime(e.target.value)}
          style={inputStyle}
        />
      </div>
      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}
      <button onClick={handleSave} disabled={saving} style={{
        width: "100%", padding: "14px", background: saving ? "#2e2e40" : "#6c63ff",
        border: "none", borderRadius: "12px", color: "#fff",
        fontSize: "15px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
      }}>
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN — TasksScreen
// ════════════════════════════════════════════════════════════════
export default function TasksScreen() {
  const { user, isMC, isPresident } = useAuth();
  const canManage = isMC();

  const [tasks, setTasks]         = useState([]);
  const [events, setEvents]       = useState([]);
  const [eventEntries, setEventEntries] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]           = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showAddChoice, setShowAddChoice]     = useState(false);
  const [showAddMaintenance, setShowAddMaintenance] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filter, setFilter]       = useState("pending"); // pending | done | all | events
  const [refreshKey, setRefreshKey] = useState(0);
  const [showArchive, setShowArchive] = useState(false);

  const [adhocTotals, setAdhocTotals] = useState({});

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const taskSnap = await getDocs(query(collection(db, "tasks"), orderBy("addedAt", "desc")));
        setTasks(taskSnap.docs.map(d => ({ docId: d.id, ...d.data() })));

        const [eventSnap, adhocSnap] = await Promise.all([
          getDocs(collection(db, "events")),
          getDocs(collection(db, "adhocEvents")),
        ]);
        const generalEvents = eventSnap.docs.map(d => ({ docId: d.id, _isSocietyWork: false, ...d.data() }));
        const societyEvents = adhocSnap.docs.map(d => ({ docId: d.id, _isSocietyWork: true, ...d.data() }));
        const eventList = [...generalEvents, ...societyEvents];
        eventList.sort((a, b) => (tsToDate(b.createdAt) || 0) - (tsToDate(a.createdAt) || 0));
        setEvents(eventList);

        const entrySnap = await getDocs(collection(db, "eventEntries"));
        setEventEntries(entrySnap.docs.map(d => ({ docId: d.id, ...d.data() })));

        // Fetch all paid adhocPayments to compute per-event income totals
        const aSnap = await getDocs(query(collection(db, "adhocPayments"), where("paid", "==", true)));
        const totals = {};
        aSnap.docs.forEach(d => {
          const data = d.data();
          const amt = Number(data.paidAmount || data.amount || 0);
          totals[data.eventId] = (totals[data.eventId] || 0) + amt;
        });
        setAdhocTotals(totals);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [refreshKey]);

  const filtered = tasks.filter(t => {
    if (filter === "pending") return !t.done;
    if (filter === "done") return t.done;
    return true;
  });

  const pendingCount = tasks.filter(t => !t.done).length;
  const doneCount    = tasks.filter(t => t.done).length;

  // Current FY starts every April 1st automatically — never hardcoded.
  const today = new Date();
  const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const FY_START = new Date(fyStartYear, 3, 1); // April is month index 3
  function isCurrentFY(ev) {
    const d = tsToDate(ev.createdAt);
    return d && d >= FY_START;
  }
  function isArchived(ev) {
    return ev.status === "closed" || !isCurrentFY(ev);
  }
  const activeEvents  = events.filter(ev => !isArchived(ev) && !ev.disabled);
  // UPDATED 2026-07-13: disabled events now show in the Archive tab
  // (not hidden entirely) — the event and its dues still exist in
  // Firestore untouched, and now Dr. Vijay can find them again later
  // instead of them vanishing from the UI completely.
  const archiveEvents = events.filter(ev => isArchived(ev) || ev.disabled);

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading tasks…</div>;
  }

  const onEventsFilter = filter === "events";

  return (
    <div style={{ padding: "16px", maxWidth: "640px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>✅ Tasks</div>
          <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>Society task tracker</div>
        </div>
      </div>

      {/* Meeting Reminder Banner — always visible to everyone */}
      <MeetingReminderBanner user={user} isPresident={isPresident()} />

      {/* Filter + counts — Events is the new 4th option */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
        {[
          { key: "pending", label: `Pending (${pendingCount})` },
          { key: "done",    label: `Done (${doneCount})` },
          { key: "all",     label: `All (${tasks.length})` },
          { key: "events",  label: `Events (${activeEvents.length})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            flex: 1, minWidth: "70px", padding: "8px", borderRadius: "10px", border: "1px solid",
            borderColor: filter === f.key ? "#6c63ff" : "#2e2e40",
            background: filter === f.key ? "#6c63ff20" : "transparent",
            color: filter === f.key ? "#6c63ff" : "#8888a8",
            fontSize: "12px", fontWeight: "600", cursor: "pointer",
          }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* "+ Add" button — shown AFTER filter chips so it knows which filter is active */}
      {canManage && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
          <button
            onClick={() => setShowAddChoice(true)}
            style={{
              background: "#6c63ff", border: "none", borderRadius: "10px",
              padding: "8px 14px", color: "#fff", fontSize: "13px", fontWeight: "600", cursor: "pointer",
            }}
          >
            + Add
          </button>
        </div>
      )}

      {/* ───────────────── EVENTS VIEW ───────────────── */}
      {onEventsFilter ? (
        <div>
          {/* Active events */}
          {activeEvents.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#555570", fontSize: "13px" }}>
              No active events.
            </div>
          ) : (
            activeEvents.map(ev => (
              <EventCard
                key={ev.docId}
                event={ev}
                entries={eventEntries}
                adhocTotals={adhocTotals}
                canManage={canManage}
                onClick={async () => { try { const fr = await getDoc(doc(db, "adhocEvents", ev.docId)); setSelectedEvent(fr.exists() ? { ...ev, ...fr.data(), docId: ev.docId, _isSocietyWork: true } : ev); } catch { setSelectedEvent(ev); } }}
                onDeleted={() => setRefreshKey(k => k + 1)}
                user={user}
              />
            ))
          )}

          {/* Archive link */}
          <div style={{ textAlign: "center", marginTop: "16px", marginBottom: "8px" }}>
            <button
              onClick={() => setShowArchive(v => !v)}
              style={{ background: "none", border: "none", color: "#6c63ff", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
            >
              {showArchive ? "▲ Hide Archive" : `▼ Archive (${archiveEvents.length})`}
            </button>
          </div>

          {/* Archive list */}
          {showArchive && (
            <div style={{ opacity: 0.75 }}>
              <div style={{ fontSize: "11px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600", marginBottom: "8px" }}>
                Archived Events
              </div>
              {archiveEvents.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px", color: "#555570", fontSize: "13px" }}>No archived events.</div>
              ) : (
                archiveEvents.map(ev => (
                  <EventCard
                    key={ev.docId}
                    event={ev}
                    entries={eventEntries}
                    adhocTotals={adhocTotals}
                    canManage={canManage}
                    onClick={async () => { try { const fr = await getDoc(doc(db, "adhocEvents", ev.docId)); setSelectedEvent(fr.exists() ? { ...ev, ...fr.data(), docId: ev.docId, _isSocietyWork: true } : ev); } catch { setSelectedEvent(ev); } }}
                    onDeleted={() => setRefreshKey(k => k + 1)}
                    user={user}
                  />
                ))
              )}
            </div>
          )}
        </div>
      ) : (
        /* ───────────────── CHECKLIST VIEW — UNCHANGED ───────────────── */
        filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#555570", fontSize: "13px" }}>
            {filter === "pending" ? "No pending tasks. 🎉" : "No tasks found."}
          </div>
        ) : (
          filtered.map(task => {
            const due = dueDateBadge(task.due);
            return (
              <div key={task.docId} style={{
                background: "#1a1a24",
                border: `1px solid ${task.done ? "#2e2e40" : "#3e3e54"}`,
                borderRadius: "14px", padding: "14px", marginBottom: "10px",
                opacity: task.done ? 0.7 : 1,
              }}>
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <div
                    onClick={() => canManage && setSelectedTask(task)}
                    style={{
                      width: "20px", height: "20px", borderRadius: "6px", flexShrink: 0, marginTop: "1px",
                      border: `2px solid ${task.done ? "#4caf50" : "#2e2e40"}`,
                      background: task.done ? "#4caf5020" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: canManage ? "pointer" : "default",
                    }}
                  >
                    {task.done && <span style={{ fontSize: "12px", color: "#4caf50" }}>✓</span>}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: "13px", color: task.done ? "#555570" : "#e8e8f0",
                      fontWeight: "600", textDecoration: task.done ? "line-through" : "none",
                      lineHeight: "1.4",
                    }}>
                      {task.text}
                    </div>

                    <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap", alignItems: "center" }}>
                      {task.due && (
                        <span style={{
                          fontSize: "11px", padding: "2px 8px", borderRadius: "6px",
                          background: due.bg, border: `1px solid ${due.border}`, color: due.color,
                          fontWeight: "600",
                        }}>
                          {due.label}
                        </span>
                      )}
                      <span style={{ fontSize: "11px", color: "#555570" }}>
                        Added by {task.addedBy}
                      </span>
                    </div>

                    {task.done && task.doneNote && (
                      <div style={{
                        marginTop: "8px", fontSize: "11px", color: "#4caf50",
                        background: "#4caf5010", borderRadius: "6px", padding: "5px 8px",
                      }}>
                        ✓ {task.doneNote}
                      </div>
                    )}
                  </div>

                  {canManage && (
                    <button
                      onClick={() => setSelectedTask(task)}
                      style={{
                        background: "none", border: "none", color: "#555570",
                        fontSize: "16px", cursor: "pointer", padding: "2px",
                      }}
                    >
                      {task.done ? "↩" : "✓"}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )
      )}

      {/* Modals */}
      {showAdd && (
        <AddTaskModal
          user={user}
          onClose={() => setShowAdd(false)}
          onDone={() => { setShowAdd(false); setRefreshKey(k => k + 1); }}
        />
      )}

      {/* Choice prompt — shown when + Add is tapped on Events filter */}
      {showAddChoice && (
        <ModalShell title="What would you like to add?" onClose={() => setShowAddChoice(false)}>
          <div style={{ fontSize: "13px", color: "#8888a8", marginBottom: "16px" }}>
            Choose where this amount should go:
          </div>
          <button
            onClick={() => { setShowAddChoice(false); setShowAddMaintenance(true); }}
            style={{
              width: "100%", marginBottom: "10px", padding: "14px",
              background: "#4caf5020", border: "1px solid #4caf5040",
              borderRadius: "12px", color: "#4caf50",
              fontSize: "14px", fontWeight: "700", cursor: "pointer",
            }}
          >
            💰 Add to Maintenance
            <div style={{ fontSize: "11px", fontWeight: "400", marginTop: "4px", color: "#4caf5099" }}>
              Lump sum income added directly to society balance
            </div>
          </button>
          <button
            onClick={() => { setShowAddChoice(false); setShowCreateEvent(true); }}
            style={{
              width: "100%", padding: "14px",
              background: "#6c63ff20", border: "1px solid #6c63ff40",
              borderRadius: "12px", color: "#6c63ff",
              fontSize: "14px", fontWeight: "700", cursor: "pointer",
            }}
          >
            📋 Create Event
            <div style={{ fontSize: "11px", fontWeight: "400", marginTop: "4px", color: "#6c63ff99" }}>
              Track income and expenses for a specific event separately
            </div>
          </button>
        </ModalShell>
      )}

      {showAddMaintenance && (
        <AddMaintenanceIncomeModal
          user={user}
          onClose={() => setShowAddMaintenance(false)}
          onDone={() => { setShowAddMaintenance(false); setRefreshKey(k => k + 1); }}
        />
      )}
      {selectedTask && (
        <DoneModal
          task={selectedTask}
          user={user}
          onClose={() => setSelectedTask(null)}
          onDone={() => { setSelectedTask(null); setRefreshKey(k => k + 1); }}
        />
      )}
      {showCreateEvent && (
        <CreateEventModal
          user={user}
          onClose={() => setShowCreateEvent(false)}
          onDone={() => { setShowCreateEvent(false); setRefreshKey(k => k + 1); }}
        />
      )}
      {selectedEvent && (
        <EventDetailScreen
          event={selectedEvent}
          user={user}
          isMC={canManage}
          onClose={() => setSelectedEvent(null)}
          onChanged={() => { setRefreshKey(k => k + 1); setSelectedEvent(null); }}
        />
      )}
    </div>
  );
}
